import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { SimNotice } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import VerifyCode from "@/components/VerifyCode";
import AuthShell from "@/components/AuthShell";
import { StepProduct, StepPersonal, StepContact, StepLogin } from "@/components/signup/Steps";

const STEPS = [["Choose account", StepProduct], ["About you", StepPersonal], ["Contact", StepContact], ["Create login", StepLogin]];
const empty = { product: "checking_investing", name: "", dob: "", street: "", city: "", state: "", zip: "", email: "", phone: "", username: "", password: "", confirm: "", agree: false };

const age = (dob) => {
  const d = new Date(`${dob}T00:00:00`), t = new Date();
  return t.getFullYear() - d.getFullYear() - (t.getMonth() < d.getMonth() || (t.getMonth() === d.getMonth() && t.getDate() < d.getDate()) ? 1 : 0);
};

const stepError = (step, f) => {
  if (step === 1) {
    if (!f.dob || Number.isNaN(age(f.dob))) return "Enter a valid date of birth";
    if (age(f.dob) < 18) return "You must be at least 18 to open an account";
    if (!/^\d{5}(-\d{4})?$/.test(f.zip.trim())) return "Enter a valid ZIP code";
  }
  if (step === 2 && f.phone.replace(/\D/g, "").length < 10) return "Enter a valid phone number";
  return "";
};

export default function OpenAccount() {
  const { user } = useAuth();
  const [f, setF] = useState(empty);
  const [step, setStep] = useState(0);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  if (user) return <Navigate to="/" replace />;
  const [title, Step] = STEPS[step];
  const last = step === STEPS.length - 1;

  const next = async (e) => {
    e.preventDefault();
    setError("");
    const se = stepError(step, f);
    if (se) return setError(se);
    if (!last) return setStep(step + 1);
    if (f.password !== f.confirm) return setError("Passwords do not match");
    if (!f.agree) return setError("Please accept the account agreement");
    setBusy(true);
    try {
      const { confirm, ...body } = f;
      const r = await api.post("/auth/signup", body);
      setPending(r.data);
      toast.success(`Verification code sent to ${r.data.email}`);
    } catch (err) { setError(errMsg(err)); }
    setBusy(false);
  };

  return (
    <AuthShell>
      {pending ? <VerifyCode pending={pending} onBack={() => setPending(null)} /> : (
        <form onSubmit={next} data-testid="signup-form">
          <h1 className="text-3xl font-extrabold text-[var(--navy)]">Open an account</h1>
          <div className="flex gap-2 mt-5 mb-2" data-testid="signup-progress">
            {STEPS.map(([t], i) => <div key={t} className={`h-1.5 flex-1 rounded-full ${i <= step ? (i === step ? "bg-[var(--red)]" : "bg-[var(--blue)]") : "bg-slate-200"}`} />)}
          </div>
          <p className="text-sm text-slate-500 mb-6" data-testid="signup-step-title">Step {step + 1} of {STEPS.length} · {title}</p>
          <Step f={f} set={set} />
          {error && <div className="mt-4 text-sm text-[var(--red)] bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="signup-error">{error}</div>}
          <div className="flex gap-3 mt-6">
            {step > 0 && <Button type="button" variant="outline" className="h-11 rounded-full px-6" onClick={() => setStep(step - 1)} data-testid="signup-back-button">Back</Button>}
            <Button disabled={busy} className="flex-1 h-11 rounded-full bg-[var(--blue)] hover:bg-[var(--navy)]" data-testid="signup-next-button">{busy ? "Sending code..." : last ? "Submit application" : "Continue"}</Button>
          </div>
          <p className="text-sm text-slate-500 text-center mt-6">Already enrolled? <Link to="/login" className="font-semibold text-[var(--blue)]" data-testid="signup-login-link">Sign in</Link></p>
        </form>
      )}
      <SimNotice className="mt-8" />
    </AuthShell>
  );
}
